import SettingsGroup from "components/CRM/SettingsGroup";
import React, { useEffect, useState, Fragment } from "react";
import { Button, Row, Col } from "reactstrap";
import WidgetRenderer from "../WidgetRenderer";
import FiltersGroup from "./FiltersGroup";
import NameDescriptionGroup from "./NameDescriptionGroup";
import DropdownOptionWithIcon from "components/Menus/DropdownOptionWithIcon";
import StrUtils from "utils/StrUtils";
import useModule from "hooks/useModule";
import useSortableFields from "hooks/useSortableFields";
import AppDrawer from "components/Drawers/AppDrawer";
import ModulePicker from "components/Modules/ModulePicker";
import SubModuleFields from "components/ManageSubModules/SubModuleFields";
import SubModulePrefill from "components/ManageSubModules/SubModulePrefill";
import _ from "underscore";
import PopoverDropdownMenu from "components/Menus/PopoverDropdownMenu";
import DropdownMenuDivider from "components/Menus/DropdownMenuDivider";
import { AppConfirm } from "utils/PopupUtil";
import CustomToggle from "components/Drawers/Fields/CustomToggle";
import Constants from "../../../utils/Constants";

const NoDataSource = () => (
  <h5 className="my-2 text-center">Select A Data Source</h5>
);

const TableWidgetSettings = ({
  open,
  onClose,
  section,
  setSettings,
  updateWidget,
  theme,
}) => {
  const [prevWidget, setPrevWidget] = useState(null);
  const [widget, setWidget] = useState(null);
  const { module } = useModule(widget?.ModuleAPIName);
  const { sortableFields } = useSortableFields(widget?.ModuleAPIName);

  const handleClose = async () => {
    // Save the widget in case the user wants to continue editing.
    const widgetSnapshot = widget;
    onClose();
    setPrevWidget(null);

    if (JSON.stringify(widget) === JSON.stringify(section)) return;

    const result = await AppConfirm({
      title: "Unsaved Changes",
      text: "You have made changes to the settings. Do you want to save them?",
      showDenyButton: true,
      confirmButtonColor: "var(--primary)",
      confirmButtonText: "Save Changes",
      denyButtonColor: "var(--danger)",
      denyButtonText: "Discard Changes",
      cancelButtonColor: "var(--secondary)",
      cancelButtonText: "Continue Editing",
    });

    if (result.isDenied) {
      return;
    }
    if (result.isConfirmed) {
      return handleSave();
    }

    setPrevWidget(widgetSnapshot);
    setSettings(section);
  };

  const handleSave = () => {
    updateWidget(widget);
    onClose();
  };

  const updateSection = (prop, value) => {
    setWidget((prev) => {
      prev[prop] = value;
      return { ...prev };
    });
  };

  const handleModuleChange = async (newModule) => {
    if (!section?.ModuleAPIName) {
      return updateSection("ModuleAPIName", newModule);
    }

    const result = await AppConfirm({
      title: "Change Reference Module?",
      text: "Changing the reference module will cause you to lose all added fields, filters, and quick creates.",
      confirmButtonColor: "var(--primary)",
      confirmButtonText: "Yes, change it!",
    });

    if (result.isConfirmed) {
      const newQuery = {
        Fields: ["_RecordName", "id"],
        Filters: [],
        Pagination: {
          Count: 25,
          Page: 1,
        },
      };

      updateSection("Query", newQuery);
      updateSection("ModuleAPIName", newModule);
    }
  };

  const handleFieldsUpdate = (newFields) => {
    const newQuery = {
      ...widget.Query,
      Fields: newFields,
    };

    updateSection("Query", newQuery);
  };

  const updateSortBy = (sortBy) => {
    let newQuery = { ...widget?.Query };

    newQuery.OrderBy = sortBy ? sortBy : null;

    if (newQuery.OrderBy === null) {
      delete newQuery.OrderBy;
    }

    updateSection("Query", newQuery);
  };

  const handleShowToolbarChange = (e) => {
    const value = e.target.checked;

    updateSection("ShowToolbar", value);
  };

  const handleDeletePrefill = (prefillID) => {
    setWidget((prev) => {
      prev.Prefills = prev.Prefills.filter(
        (prefill) => prefill.id !== prefillID
      );

      return { ...prev };
    });
  };

  const handleReorderPrefill = (newPrefillOrder) => {
    setWidget((prev) => {
      const newPrefills = [];

      for (const id of newPrefillOrder) {
        const prefill = _.findWhere(prev.Prefills, { id });
        newPrefills.push(prefill);
      }

      prev.Prefills = newPrefills;

      return { ...prev };
    });
  };

  useEffect(() => {
    if (prevWidget) {
      setWidget({ ...prevWidget });
    } else {
      setWidget({ ...section });
    }
  }, [section, prevWidget]);

  return (
    <AppDrawer
      size="full"
      placement="bottom"
      bodyStyle={{ padding: 0 }}
      open={open}
      onClose={handleClose}
      headerTitle={`${section?.Name} Settings`}
      headerOptions={
        <Button color="primary" size="sm" onClick={handleSave}>
          Save
        </Button>
      }
      body={
        <div className="d-flex" style={{ height: "100%" }}>
          <div
            className="py-4"
            style={{
              width: "calc(100% - 350px)",
            }}
          >
            <WidgetRenderer
              section={widget}
              setSettings={null}
              useTableToolbar={false}
            />
          </div>
          <div
            style={{ minWidth: 350, overflowY: "auto", maxWidth: 350 }}
            className="border-left p-4"
          >
            <NameDescriptionGroup
              widget={widget}
              updateSection={updateSection}
              theme={Constants.DASHBOARD_COLOR_THEMES?.[theme]}
            />
            {/* <SettingsGroup title="Widget Style" className="mb-2">
              <DefaultWidgetStyles
                widget={widget}
                updateSection={updateSection}
                theme={Constants.DASHBOARD_COLOR_THEMES?.[theme]}
              />
            </SettingsGroup> */}
            <SettingsGroup title="Data Source" className="mb-2">
              <h5 className="font-weight-500">Module:</h5>
              <ModulePicker
                value={widget?.ModuleAPIName}
                onChange={handleModuleChange}
              />
            </SettingsGroup>
            <SettingsGroup
              title="Fields"
              className="mb-2"
              externalTrigger={section?._OpenFields ?? null}
              closeExternalTrigger={
                section?._OpenFields
                  ? () => {
                      delete section._OpenFields;
                    }
                  : null
              }
            >
              {!widget?.ModuleAPIName ? (
                <NoDataSource />
              ) : (
                <SubModuleFields
                  fields={widget?.Query?.Fields}
                  onFieldUpdate={handleFieldsUpdate}
                  moduleAPIName={widget?.ModuleAPIName}
                />
              )}
            </SettingsGroup>
            {!widget?.ModuleAPIName ? (
              <SettingsGroup title="Filters" className="mb-2">
                <NoDataSource />
              </SettingsGroup>
            ) : (
              <FiltersGroup
                widget={widget}
                updateSection={updateSection}
                module={module}
              />
            )}
            <SettingsGroup title="Sort By" className="mb-2">
              {!widget?.ModuleAPIName ? (
                <NoDataSource />
              ) : (
                <PopoverDropdownMenu>
                  <div className="px-2 pt-2 ">
                    <Row className="align-items-center">
                      <Col xs="">
                        <h5 className="m-0 text-dark">
                          <i className="mdi mdi-swap-vertical pr-2"></i>Sort
                          Records By
                        </h5>
                      </Col>
                    </Row>
                  </div>
                  <DropdownMenuDivider></DropdownMenuDivider>
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "scroll",
                      overflowX: "hidden",
                    }}
                  >
                    {sortableFields?.map((field) => (
                      <Fragment key={field.APIName}>
                        <DropdownOptionWithIcon
                          name={field.Name}
                          onClick={(e) => {
                            if (
                              widget?.Query?.OrderBy?.Field === field.APIName &&
                              widget?.Query?.OrderBy?.Direction === "ASC"
                            ) {
                              return updateSortBy(null);
                            }
                            updateSortBy({
                              Field: field.APIName,
                              Direction:
                                widget?.Query?.OrderBy?.Field === field.APIName
                                  ? "ASC"
                                  : "DESC",
                            });
                          }}
                          icon={StrUtils.iconForField(field)}
                          active={
                            widget?.Query?.OrderBy?.Field === field.APIName
                          }
                          actionIcon={
                            widget?.Query?.OrderBy?.Field === field.APIName
                              ? widget?.Query?.OrderBy?.Direction === "DESC"
                                ? "mdi mdi-sort-descending"
                                : "mdi mdi-sort-ascending"
                              : ""
                          }
                        ></DropdownOptionWithIcon>
                      </Fragment>
                    ))}
                  </div>
                </PopoverDropdownMenu>
              )}
            </SettingsGroup>
            <SettingsGroup
              title="Quick Create"
              className="mb-2"
              externalTrigger={section?._OpenQuickCreate ?? null}
              closeExternalTrigger={
                section?._OpenQuickCreate
                  ? () => {
                      delete section._OpenQuickCreate;
                    }
                  : null
              }
            >
              {!widget?.ModuleAPIName ? (
                <NoDataSource />
              ) : (
                <div style={{ width: 280 }}>
                  <SubModulePrefill
                    module={module}
                    prefills={widget?.Prefills}
                    onUpdate={(newPrefills) => {
                      updateSection("Prefills", newPrefills);
                    }}
                    onDelete={handleDeletePrefill}
                    onReorder={handleReorderPrefill}
                  />
                </div>
              )}
            </SettingsGroup>
            <div style={{ width: "100%" }}>
              <SettingsGroup title="Show/Hide Toolbar">
                <div style={{ width: 280 }}>
                  <CustomToggle
                    title="Show Toolbar"
                    description="Choosing Yes shows the Table Widget's toolbar."
                    value={widget?.ShowToolbar}
                    handleChange={handleShowToolbarChange}
                  />
                </div>
              </SettingsGroup>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default TableWidgetSettings;
